import { localStore } from '.';

class Translator {
  constructor() {
    this.translations = {};
    this.ignoreWarnings = false;
    this.languages = [];
    this.availableLanguages = [];
    this.defaultLanguage = 'EN';
    this.isReady = false;
    this.models = {};
  }

  getProp( key, obj ) {
    let translation = key;
    const translationKey = key && key.toLowerCase ? key.toLowerCase() : key;
    if( obj ) {
      translation = obj[translationKey];
    } else {
      for ( let language of this.languages ) {
        const currentTranslation = this.translations[language] && this.translations[language][translationKey];
        if( currentTranslation || currentTranslation == undefined|| currentTranslation != null) {
          translation = currentTranslation;
          break;
        }
      }
    }

    if ( translation === undefined ||translation === null) {
      !this.ignoreWarnings && console.warn( `"${key}" - translation key could not be found` );
      return key;
    }
    
    return translation;
  }

  hasModel( model, category = 'model' ) {
    return this.models[model] && this.models[model][category] && this.languages.all( lang => this.models[model][category][lang] );
  }

  loadModel( model, category, languages, values ) {
    if ( !this.models[model] ) {
      this.models[model] = {};
    }
    for ( let lang of languages ) {
      if ( !values[lang] ) {
        continue;
      }
      this.models[model][category] = { [lang]: values[lang] };
    }
  }

  replace( translatedText, replaceFcn ) {
    return translatedText.replace( /((?:{{)*)(?:(?:{(?:([^:{}]+):)?([^{}]*)})|([^{}]+))((?:}})*)/g, replaceFcn );
  }

  /**
   * 
   * @param {string} key - string to translate
   * @param {object} replacements - contains key/value pairs
   * @return {string} - returns the translated text
   */
  get( key, replacements ) {
    if( !key ){
      return;
    }
    if( !replacements && !key.includes( '{{' ) ) {
      return this.lookup( key );
    }

    let replaceFcn = ( fullMatch, curlyBracketsStart, task, key, literalString, curlyBracketsEnd ) =>
      this.replaceFn( replacements, fullMatch, curlyBracketsStart, task, key, literalString, curlyBracketsEnd );

    return this.replace( this.lookup( key ), replaceFcn );
  }

  /**
   * @param {object} replacements - contains key/value pairs
   * @param {string} fullMatch - complete matched string
   * @param {string} curlyBracketsStart - contains even number of '{' characters
   * @param {string} task - contains task
   * @param {string} key - contains key
   * @param {string} literalString - contains string between even number of curly brackets
   * @param {string} curlyBracketsEnd - contains even number of '}' characters
   * @returns {string} the string after all replacements were made
   */
  replaceFn( replacements, fullMatch, curlyBracketsStart, task, key, literalString, curlyBracketsEnd ) {
    let text = '';

    if ( key && ( !replacements || replacements[key.toLowerCase()] === undefined ) ) {
      !this.ignoreWarnings && console.warn( 'translation failed due to missing replacement key: ' + key );
      return fullMatch;
    }

    if ( curlyBracketsStart ) {
      text += curlyBracketsStart.substr( 0, curlyBracketsStart.length / 2 );
    }

    if ( literalString ) {
      text += literalString;
    } else {
      let replacedContent = this.getProp( key, replacements );
      if ( task && task === 'tr' ) { //should translate
        text += this.get( replacedContent );
      } else {
        text += replacedContent;
      }
    }

    if ( curlyBracketsEnd ) {
      text += curlyBracketsEnd.substr( 0, curlyBracketsEnd.length / 2 );
    }

    return text;
  }

  lookup( key ) {
    if ( !this.translations ) {
      !this.ignoreWarnings && console.warn( '%c trying to translate, but no translations are loaded yet', 'color: red' );
      return key;
    }

    return this.getProp( key );
  }

  setLanguage( language, persistAcrossSesssion = false ) {
    const languageKey = this.getLanguageKey( language );

    if( !this.defaultLanguage ) {
      this.defaultLanguage = languageKey;
      this.languages = [languageKey];
      return;
    }

    persistAcrossSesssion && localStore.set( 'language', languageKey );
    this.languages = [languageKey, this.defaultLanguage];
  }

  setDictionary( dictionary ) {
    for ( let language of dictionary ) {
      const languageKey = this.getLanguageKey( language.culture );
      this.availableLanguages.push( languageKey );
      this.translations[languageKey] = language.translations;
    }

    if( !this.availableLanguages.includes( this.languages.first() ) ) {
      this.setLanguage( this.defaultLanguage );
    }

    this.isReady = true;
    return this.translations;
  }

  getLanguageKey( language ) {
    return language.split( '-' )[0].toUpperCase();
  }

  getModelTranslation( model, key, language, category = 'model' ) {
    return this.hasModel( model, category ) && this.models[model][category][language][key];
  }
}

//share one translator in the whole application
export const translator = new Translator();
export const isTranslatorReady = () => translator.isReady;
export const translate = ( text, replacements ) => translator.get( text, replacements );
export const setDictionary = translations => translator.setDictionary( translations );
export const ignoreMissingTranslations = () => translator.ignoreWarnings = true;
export const setLanguage = ( language, persistAcrossSesssion ) => translator.setLanguage( language, persistAcrossSesssion );
export const getLanguages = () => translator.languages;
export const getAvailableLanguages = () => translator.availableLanguages;
export const getLanguageKey = language => translator.getLanguageKey( language );
export const getModelTranslation = ( model, key, language, category = 'model' ) => translator.getModelTranslation( model, key, language, category );
export const hasModel = ( model, category = 'model' ) => translator.hasModel( model, category );
export const loadModel = ( model, category = 'model', languages = ['en'], values = {} ) => translator.loadModel( model, category, languages, values );
